import React, { useState, useEffect, useMemo, useCallback } from "react";
import _ from "lodash";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../../common/components/loading/loading";
import { getRoleHistory } from "../../../common/services/auth/roleService";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";

const RoleHistory = ({ roleId }) => {
  const { t } = useTranslation(["auth"]);
  const [roleHistory, setRoleHistory] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(true);

  const mapToViewModel = useCallback((roles) => {
    const dateFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "numeric",
      hour: "numeric",
      hour12: false,
      minute: "numeric",
      second: "numeric",
    };

    return roles.map((role) => ({
      id: role.id,
      name: role.name,
      description: role.description,
      active: role.active,
      updatedByUserAccountId: role.updatedByUserAccountId,
      updatedBy:
        role.updatedByUserAccountFirstName +
        " " +
        role.updatedByUserAccountLastName,
      updatedDate: new Date(role.updatedDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      writeDate: new Date(role.writeDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      eventId: role.eventId,
    }));
  }, []);

  useEffect(() => {
    const fetchRoleHistory = async () => {
      try {
        const historyData = await getRoleHistory(roleId);
        const mappedData = mapToViewModel(historyData);
        setRoleHistory(_.orderBy(mappedData, "id", "desc"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchRoleHistory();
  }, [roleId, mapToViewModel]);

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      { field: "id", headerName: t("RecordID"), flex: 1 },
      { field: "name", headerName: t("RoleName"), flex: 2 },
      { field: "description", headerName: t("RoleDescription"), flex: 2.5 },
      {
        field: "active",
        headerName: t("Active"),
        flex: 1,
        // Apply centered class for better alignment
        cellClass: "ag-center-aligned-cell",
        headerClass: "ag-center-aligned-header",
        filterValueGetter: (params) => params.data.activeString,
        cellRenderer: (params) => {
          const isActive = params.data?.active;
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <FontAwesomeIcon
                icon={isActive ? faCheck : faTimes}
                className={isActive ? "text-success" : "text-danger"}
                style={{ fontSize: "16px" }}
              />
            </div>
          );
        },
      },
      { field: "updatedBy", headerName: t("UpdatedBy"), flex: 2 },
      { field: "updatedDate", headerName: t("UpdateDate"), flex: 1.5 },
      { field: "writeDate", headerName: t("WriteDate"), flex: 1.5 },
      { field: "eventId", headerName: t("EventID"), width: 120 },
    ],
    [t]
  );

  // Memoize default column properties
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 100,
    }),
    []
  );

  // Pagination configuration
  const paginationConfig = useMemo(
    () => ({
      enabled: true,
      pageSize: 10,
      pageSizeOptions: [5, 10, 20, 50, 100],
    }),
    []
  );

  // Handle page size change
  const handlePageSizeChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
  }, []);

  if (isLoading) return <Loading />;

  return (
    <div className="history-table-container">
      <div className="mb-2">
        {t("RoleID")}: {roleId}
      </div>
      <ThemeableAgGrid
        className="table-extra-small"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowData={roleHistory}
        getRowId={(params) => String(params.data.id)}
        // Normal layout for pagination
        domLayout="autoHeight"
        // Enable pagination
        pagination={true}
        paginationPageSize={pageSize}
        paginationPageSizeSelector={paginationConfig.pageSizeOptions}
        onPaginationChanged={(event) => {
          const newPageSize = event.api.paginationGetPageSize();
          if (newPageSize !== pageSize) {
            handlePageSizeChange(newPageSize);
          }
        }}
        suppressPaginationPanel={false}
        // Fixed height for consistent UI
        height="400px"
        animateRows={true}
      />
    </div>
  );
};

export default React.memo(RoleHistory);

